import { useState, type FormEvent } from 'react';
import type { CreateRetainerInput, Retainer } from '@dsr/shared-types';
import {
  useCreateRetainer,
  useDeleteRetainer,
  useProjects,
  useRetainers,
} from '../api/queries/financials';
import { useOrganizations } from '../api/queries/clients';
import { DateInput } from '../components/DateInput';
import { toast } from '../components/Toasts';
import { can } from '../auth/store';
import { ApiError } from '../api/client';
import { fmtDate } from '../utils/date';
import { fmtMoney } from '../utils/money';
import {
  Button,
  ConfirmDialog,
  Dialog,
  FormField,
  Input,
  Table,
  Textarea,
} from '../components/ui';

const MONEY_RE = /^\d+(\.\d{1,2})?$/;

export function RetainersPage(): JSX.Element {
  const [activeOnly, setActiveOnly] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [removing, setRemoving] = useState<Retainer | null>(null);

  const { data, isLoading, isError } = useRetainers(activeOnly ? { active: true } : {});
  const del = useDeleteRetainer();
  const canWrite = can('retainer.write');
  const canDelete = can('retainer.delete');
  const retainers = data?.retainers ?? [];

  return (
    <section className="page retainers-page">
      <header className="page-header">
        <div>
          <h1>Retainers</h1>
          <p className="page-sub">
            Prepaid client balances. Draw-downs happen when an invoice is
            marked paid with a retainer selected.
          </p>
        </div>
        {canWrite && (
          <div className="page-toolbar">
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              Fund retainer
            </Button>
          </div>
        )}
      </header>

      <div className="filters-bar">
        <label className="form-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
          />
          <span>Only retainers with a balance</span>
        </label>
      </div>

      {isLoading && <p className="muted">Loading…</p>}
      {isError && <p className="error-text" role="alert">Failed to load retainers.</p>}
      {!isLoading && !isError && retainers.length === 0 && (
        <p className="muted">No retainers{activeOnly ? ' with a balance' : ''}.</p>
      )}

      {retainers.length > 0 && (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Client</Table.HeaderCell>
              <Table.HeaderCell>Project</Table.HeaderCell>
              <Table.HeaderCell>Effective</Table.HeaderCell>
              <Table.HeaderCell>Funded</Table.HeaderCell>
              <Table.HeaderCell>Balance</Table.HeaderCell>
              {canDelete && <Table.HeaderCell aria-label="Actions" />}
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {retainers.map((r) => (
              <Table.Row key={r.id}>
                <Table.Cell tone="strong">{r.organization_name ?? '—'}</Table.Cell>
                <Table.Cell tone="muted">{r.project_title ?? 'Client-wide'}</Table.Cell>
                <Table.Cell>{fmtDate(r.effective_from)}</Table.Cell>
                <Table.Cell>{fmtMoney(r.initial_amount)}</Table.Cell>
                <Table.Cell tone="strong">{fmtMoney(r.current_balance)}</Table.Cell>
                {canDelete && (
                  <Table.Cell>
                    {Number(r.current_balance) === 0 && (
                      <Button variant="ghost" onClick={() => setRemoving(r)}>
                        Remove
                      </Button>
                    )}
                  </Table.Cell>
                )}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {createOpen && <FundRetainerModal onClose={() => setCreateOpen(false)} />}
      {removing && (
        <ConfirmDialog
          open
          title="Remove retainer"
          message="Remove this fully-drawn retainer from the list? Its history stays in the audit log."
          confirmLabel="Remove"
          danger
          busy={del.isPending}
          onConfirm={() => {
            del
              .mutateAsync(removing.id)
              .then(() => toast.success('Retainer removed.'))
              .catch((err) =>
                toast.danger(err instanceof ApiError ? err.message : 'Remove failed.'),
              )
              .finally(() => setRemoving(null));
          }}
          onCancel={() => setRemoving(null)}
        />
      )}
    </section>
  );
}

function FundRetainerModal({ onClose }: { onClose: () => void }): JSX.Element {
  const [orgId, setOrgId] = useState<number | ''>('');
  const [projectId, setProjectId] = useState<number | ''>('');
  const [amount, setAmount] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState<number | null>(Date.now());
  const [recordPayment, setRecordPayment] = useState(true);
  const [notes, setNotes] = useState('');

  const { data: orgsData } = useOrganizations({});
  const { data: projectsData } = useProjects(orgId === '' ? {} : { organization_id: orgId });
  const create = useCreateRetainer();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (orgId === '' || !effectiveFrom || !MONEY_RE.test(amount)) return;
    const input: CreateRetainerInput & { record_funding_payment?: boolean } = {
      organization_id: orgId,
      initial_amount: amount,
      effective_from: effectiveFrom,
    };
    if (projectId !== '') input.project_id = projectId;
    if (notes.trim()) input.notes = notes.trim();
    if (recordPayment) input.record_funding_payment = true;

    try {
      await create.mutateAsync(input);
      toast.success('Retainer funded.');
      onClose();
    } catch (err) {
      toast.danger(err instanceof ApiError ? err.message : 'Failed to fund retainer.');
    }
  };

  return (
    <Dialog
      title="Fund retainer"
      open
      onClose={onClose}
      busy={create.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="fund-retainer-form"
            variant="primary"
            disabled={create.isPending || orgId === '' || !effectiveFrom || !MONEY_RE.test(amount)}
          >
            {create.isPending ? 'Saving…' : 'Fund'}
          </Button>
        </>
      }
    >
      <form id="fund-retainer-form" className="form-grid" onSubmit={onSubmit}>
        <FormField label="Client" required>
          <select
            className="form-input"
            value={orgId === '' ? '' : String(orgId)}
            onChange={(e) => {
              setOrgId(e.target.value === '' ? '' : Number(e.target.value));
              setProjectId('');
            }}
            required
          >
            <option value="">Select a client…</option>
            {(orgsData?.organizations ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Scope">
          <select
            className="form-input"
            value={projectId === '' ? '' : String(projectId)}
            onChange={(e) => setProjectId(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <option value="">Client-wide</option>
            {(projectsData?.projects ?? []).map((p) => (
              <option key={p.id} value={p.id}>{p.title}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Amount" required>
          <Input
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="10000.00"
            inputMode="decimal"
            required
          />
        </FormField>
        <FormField label="Effective from" required>
          <DateInput value={effectiveFrom} onChange={setEffectiveFrom} required />
        </FormField>
        <label className="form-field form-field-full" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            checked={recordPayment}
            onChange={(e) => setRecordPayment(e.target.checked)}
          />
          <span>Also record the funding in the income ledger</span>
        </label>
        <FormField label="Notes" full>
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </FormField>
      </form>
    </Dialog>
  );
}
